import React from 'react';
import './PulsePage.css';
import { useNavigate } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { PivotTableData } from '@/models/PulseData';
import SectionHeader, { HeaderControl } from '../common/SectionHeader';
import { faPrint, faFileClipboard, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import logger from '../../utils/logger';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { useNotifier } from '../../hooks/useNotifier';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TimeSliceViewType, TimeSliceWindow } from '@/models/TimeSlice';
import { DatePicker, TimePicker } from '../ui/TimePicker';

export type SummaryType = string; // 'pivot' or 'service_SERVICE_NAME'
export type NotifierType = 'success' | 'error' | 'warning' | 'info';

interface PulseNavProps {
  activeSummary: SummaryType;
  urlPathToParent: string;
  onSelectSummary: (summary: SummaryType) => void;
  services: PivotTableData['rows'];
  keyAttributes: string[];
  servicesGroup: string;
  pulseView: TimeSliceViewType;
  onPulseViewChange: (view: TimeSliceViewType) => void;  
  timeSliceWindow: TimeSliceWindow,
  currentEndDate: Date;
  onEndDateChange: (date: Date) => void;
  minSelectableDate: string; // YYYY-MM-DD
  maxSelectableDate: string; // YYYY-MM-DD  
}

const PulseNav: React.FC<PulseNavProps> = ({ 
  activeSummary, urlPathToParent, onSelectSummary, 
  services, keyAttributes, servicesGroup,
  pulseView, onPulseViewChange,
  timeSliceWindow, currentEndDate, onEndDateChange,
  minSelectableDate,
  maxSelectableDate  
}) => {
  
  const navigate = useNavigate();

  const { showNotification } = useNotifier();
  const dateInputRef = React.useRef<HTMLInputElement>(null);

  const handleNavControlClick = async (action: string) => {

    logger.info(`PulseNav control clicked: ${action}`);

    switch (action) {
      case 'Copy Services Data (raw)':
        try {
          const navDataString = JSON.stringify(services, null, 2);
          await copyToClipboard(navDataString, showNotification, 'Services data copied to clipboard!');
        } catch (error) {
          logger.error('Error stringifying Services Data:', error);
          // alert('Could not prepare navigation data for copying.');
          showNotification('Could not prepare navigation data for copying.', 'error');
        }
        break;
      default:
      //   alert(`Action: ${action} for Navigation (Not implemented)`);
      showNotification(`Action: ${action} for Navigation (Not implemented)`, 'info');        
    }      
  };

  const handleDateInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDateValue = event.target.value; // "YYYY-MM-DD"
    const [year, month, day] = newDateValue.split('-').map(Number);

    const newEndDate = new Date(currentEndDate);
    // setFullYear handles month being 1-based from the input
    newEndDate.setFullYear(year, month - 1, day);

    onEndDateChange(newEndDate);
  };

  const handleTimeChange = (changedTime: string) => {
    const [hours, minutes] = changedTime.split(':').map(Number);

    const newEndDate = new Date(currentEndDate); // Create a copy
    newEndDate.setHours(hours, minutes, 0, 0); // Set new time, reset seconds/ms

    onEndDateChange(newEndDate);
  };

  /*
  const openDatePicker = () => {
    console.log('Opening date picker...');
    dateInputRef.current?.showPicker?.(); // Modern way to open date picker
    // Fallback for older browsers or if showPicker is not available
    if (!dateInputRef.current?.showPicker) {
      console.error('Failed to open date picker...');
      dateInputRef.current?.focus(); // Focus might sometimes trigger it
    }
  };
  */

  const navControls: HeaderControl[] = [
    { id: 'nav-copy-data', icon: faFileClipboard, tooltip: 'Copy Services Data', onClick: () => handleNavControlClick('Copy Services Data (raw)') },
  //   { id: 'nav-copy-formatted', icon: faCopy, tooltip: 'Copy Services View', onClick: () => handleNavControlClick('Copy Services View') },
    { id: 'nav-print', icon: faPrint, tooltip: 'Print Nav', onClick: () => handleNavControlClick('Print Nav') },
    // { id: 'nav-set-date', icon: faCalendarAlt, tooltip: 'Set End Date', onClick: openDatePicker },
    // we will use a local label/UI element to trigger for setting up dates
  ];


  // Format date for input type="date" which expects YYYY-MM-DD
  // const formattedEndDate = currentEndDate.toLocaleDateString('en-CA'); // 'en-US' gives YYYY-MM-DD
  // Alternative if toLocaleDateString('en-US') is not reliable across all browsers/Node versions for this format:
  // const yyyy = currentEndDate.getFullYear();
  // const mm = String(currentEndDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  // const dd = String(currentEndDate.getDate()).padStart(2, '0');
  // const formattedEndDate = `${yyyy}-${mm}-${dd}`;

  const formattedDate = (inputDate: Date) => {
    const yyyy = currentEndDate.getFullYear();
    const mm = String(currentEndDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const dd = String(currentEndDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const formattedTime = (inputDate: Date) => {
    const hh = String(inputDate.getHours()).padStart(2, '0');
    const mm = String(inputDate.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  const formattedEndDate = formattedDate(currentEndDate);
  const currentTime = formattedTime(currentEndDate);

  // For the max attribute in date picker, we need today's date in YYYY-MM-DD format
  // const getYesterday = () => {
  //   const date = new Date();
  //   date.setDate(date.getDate() - 1);
  //   return date;
  // };
 
  return (
    <nav className="pulse-nav">
        <SectionHeader 
          title={servicesGroup ?? "Summaries"}
          showBackButton={urlPathToParent != undefined}
          onBack={() => navigate(urlPathToParent)}
        />
      {/* <SectionHeader title={servicesGroup ?? "Summaries"} controls={navControls} /> */}
      <div className="pulse-view-switcher">
        <button onClick={() => onPulseViewChange('2h')} className={`deriq-action-button-sm ${pulseView === '2h' ? 'active' : ''}`}>2 hours</button>
        <button onClick={() => onPulseViewChange('24h')} className={`deriq-action-button-sm ${pulseView === '24h' ? 'active' : ''}`}>24 hours</button>
        <button onClick={() => onPulseViewChange('7d')} className={`deriq-action-button-sm ${pulseView === '7d' ? 'active' : ''}`}>7 days</button>
      </div>
      <div className="pulse-nav-date-time">
        <span>End at: {formattedEndDate}</span>
        <label htmlFor="nav-date-picker-input" className="section-header-control-button" title="Set End Date" style={{ cursor: 'pointer'}}>
            <FontAwesomeIcon icon={faCalendarAlt} />
        </label>
        <DatePicker
          pickerDate={formattedEndDate}
          onDateChange={handleDateInputChange}
          minSelectableDate={minSelectableDate}
          maxSelectableDate={maxSelectableDate}
        />
        <TimePicker
          value={currentTime}
          intervalMinutes={timeSliceWindow.incrementMinutes} // Custom interval of 30 minutes
          onChange={handleTimeChange}
          id="main-time-picker"
        />        
      </div>
      {/* Date input, visually minimal and positioned off-screen but accessible */}
      {/* It's placed outside the scrollable pulse-nav-body to avoid layout issues */}
      {/* <div style={{ height: 0, width: 0, overflow: 'hidden' }}> 
        <input
          id="nav-date-picker-input" // ID for the label to target
          type="date" 
          ref={dateInputRef}
          value={formattedEndDate}
          onChange={handleDateInputChange}
          min={minSelectableDate}
          max={maxSelectableDate} // Limit selectable dates
          // style={{ opacity: 0, position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
          // No complex absolute positioning needed if it's inside a 0x0 div
          aria-hidden="true"
          tabIndex={-1}
        />
      </div> */}

      <div className="pulse-nav-body">
        <ul>
          <li>
            <button
              onClick={() => onSelectSummary('pivot')}
              className={activeSummary === 'pivot' ? 'active' : ''}
            >
              ALL (Error Pivot Table)
            </button>
          </li>
          <hr/>
          <h3>Services</h3>
          {services.map(service => {
            const summaryId = `service_${service.rowValue}`;
            return (
              <li key={service.rowValue}>
                <button
                  onClick={() => onSelectSummary(summaryId)}
                  className={activeSummary === summaryId ? 'active' : ''}
                >
                  {service.rowValue}
                </button>
              </li>
            );
          })}
        <hr/>
        <h3>Key Attributes</h3>
        {keyAttributes.map( attribute => {
          const summaryId = `attribute_${attribute}`;
          return (
            <li key={attribute}>
                <button
                  onClick={() => onSelectSummary(summaryId)}
                  className={activeSummary === summaryId ? 'active' : ''}
                >
                  {attribute}
                </button>
            </li>
          );
        })}
        </ul>
      </div>
    </nav>
  );
};

export default PulseNav;